import React, { useState, useCallback } from "react";
import { Modal } from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import { useMutation } from "react-query";

import { CloseButton, Button, HookFormCheckbox } from "components";

import styles from "./copyTerminals.module.scss";

const TERMINAL_ARRAY_NAME = "terminalsForCopy";

type FormValues = {
	terminalsForCopy: any;
	selectAll: boolean;
};

const CopyTerminalsModal = ({
	show,
	onHide,
	backdrop,
	dialogAs,
	terminals,
	appId,
	tab,
	copyTerminal,
	refetch,
}) => {
	const [isSelectAll, setIsSelectAll] = useState(false);
	const [isCopyBtnDisabled, setIsCopyBtnDisabled] = useState(true);

	const getTerminalLabel = ({ number, manufacturer, model }) => {
		return [`TS - ${number}`, manufacturer, model]
			.filter(item => item)
			.join(" - ");
	};

	const { control, register, setValue, handleSubmit, watch, formState } =
		useForm<FormValues>({
			defaultValues: {
				[TERMINAL_ARRAY_NAME]: terminals.map(terminal => ({
					terminal: false,
					label: getTerminalLabel(terminal),
					tsId: terminal?.id,
				})),
				selectAll: false,
			},
		});

	const { fields } = useFieldArray({ control, name: TERMINAL_ARRAY_NAME });

	const watchedTerminals = watch(TERMINAL_ARRAY_NAME);

	const setBtnDisable = useCallback(() => {
		const isOneChecked = watchedTerminals.some(({ terminal }) => terminal);
		if (isOneChecked) {
			setIsCopyBtnDisabled(false);
		} else {
			setIsCopyBtnDisabled(true);
		}
	}, [watchedTerminals]);

	const handleSelectTerminal = useCallback(() => {
		const isNotCheckedAll = watchedTerminals.some(
			({ terminal }) => !terminal,
		);

		setBtnDisable();
		if (isNotCheckedAll) {
			setValue("selectAll", false);
			setIsSelectAll(false);
		} else {
			setValue("selectAll", true);
			setIsSelectAll(true);
		}
	}, [setBtnDisable, setValue, watchedTerminals]);

	const handleSelectAll = useCallback(() => {
		fields.forEach((_terminal, index) => {
			setValue(`${TERMINAL_ARRAY_NAME}.${index}.terminal`, !isSelectAll);
		});
		setBtnDisable();
		setIsSelectAll(!isSelectAll);
	}, [fields, isSelectAll, setBtnDisable, setValue]);

	const { mutate } = useMutation(
		({ terminalsForCopy }: any) => {
			return Promise.any(
				terminalsForCopy
					?.filter(({ terminal }) => terminal)
					?.map(({ tsId }) => copyTerminal(tsId)),
			);
		},
		{
			onSuccess(result: any) {
				onHide();
				refetch();
				window.open(
					`/tid-request-equipment-queue/${tab}/${appId}/terminal-solutions/${result.id}`,
					"_blank",
				);
			},
		},
	);

	return (
		<Modal
			show={show}
			onHide={onHide}
			centered
			scrollable
			animation={false}
			backdrop={backdrop}
			dialogAs={dialogAs}
			size="xl"
		>
			<form onSubmit={handleSubmit(values => mutate(values))}>
				<Modal.Header>
					<Modal.Title>Copy Terminal Solution</Modal.Title>
					<CloseButton onClick={onHide} />
				</Modal.Header>
				<Modal.Body className={styles.modalBody}>
					<ul className={styles.list}>
						<li>
							<HookFormCheckbox
								register={register}
								name="selectAll"
								id="selectAll"
								label="Select All"
								onChange={handleSelectAll}
							/>
						</li>
						{fields.map(
							(field: { [key: string]: any }, index: number) => (
								<li key={field.id}>
									<HookFormCheckbox
										register={register}
										name={`${TERMINAL_ARRAY_NAME}.${index}.terminal`}
										id={`${TERMINAL_ARRAY_NAME}.${index}.terminal`}
										label={field.label}
										onChange={handleSelectTerminal}
									/>
									<input
										{...register(
											`${TERMINAL_ARRAY_NAME}.${index}.tsId`,
										)}
										defaultValue={field.tsId}
										hidden
									/>
								</li>
							),
						)}
					</ul>
				</Modal.Body>
				<Modal.Footer className="justify-content-center py-4">
					<Button
						onClick={onHide}
						value="Cancel"
						variant="outline-primary"
						className="mr-4"
					/>
					<Button
						type="submit"
						value="Copy Terminal Solution"
						variant="primary"
						disabled={isCopyBtnDisabled || formState.isSubmitting}
					/>
				</Modal.Footer>
			</form>
		</Modal>
	);
};

export default CopyTerminalsModal;
